/**
 * Small localStorage-backed persistence for the player profile and the
 * "keep coming back" stats (streaks, sparks, countries met).
 */

const PROFILE_KEY = 'phidio.profile.v2';
const STATS_KEY = 'phidio.stats.v2';
const SPARKS_KEY = 'phidio.sparks.v1';

export interface Profile {
  name: string;
  interests: string[];
  strict: boolean;
  mirror: boolean;
  soundOn: boolean;
}

export interface PlayerStats {
  /** Total strangers connected to, all time. */
  met: number;
  /** Mutual likes. */
  sparks: number;
  /** Conversations both people committed to staying in. */
  stays: number;
  /** Sparks you found again through a reconnect. */
  reunions: number;
  /** Total seconds spent in conversation. */
  seconds: number;
  /** Longest single conversation, in seconds. */
  longest: number;
  /** ISO-3166 alpha-2 codes of countries met. */
  countries: string[];
  /** Consecutive days with at least one conversation. */
  streak: number;
  /** YYYY-MM-DD of the last conversation. */
  lastPlayed: string | null;
  /** Unlocked achievement ids. */
  badges: string[];
}

/** A mutual like you can act on later. The token is the only credential. */
export interface SavedSpark {
  token: string;
  name: string;
  country: string | null;
  at: number;
}

const DEFAULT_PROFILE: Profile = {
  name: '',
  interests: [],
  strict: false,
  mirror: true,
  soundOn: true,
};

const DEFAULT_STATS: PlayerStats = {
  met: 0,
  sparks: 0,
  stays: 0,
  reunions: 0,
  seconds: 0,
  longest: 0,
  countries: [],
  streak: 0,
  lastPlayed: null,
  badges: [],
};

/** Sparks expire server-side after 12h; keep the local list in step. */
export const SPARK_TTL_MS = 12 * 60 * 60 * 1000;
const MAX_SAVED_SPARKS = 12;

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return { ...fallback, ...(JSON.parse(raw) as Partial<T>) };
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — stats are a nice-to-have, never fatal */
  }
}

export function loadProfile(): Profile {
  return read(PROFILE_KEY, DEFAULT_PROFILE);
}

export function saveProfile(profile: Profile): void {
  write(PROFILE_KEY, profile);
}

export function loadStats(): PlayerStats {
  return read(STATS_KEY, DEFAULT_STATS);
}

export function saveStats(stats: PlayerStats): void {
  write(STATS_KEY, stats);
}

/** Reads the spark list, dropping anything the server will have expired. */
export function loadSparks(): SavedSpark[] {
  try {
    const raw = localStorage.getItem(SPARKS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - SPARK_TTL_MS;
    return parsed
      .filter(
        (item): item is SavedSpark =>
          !!item &&
          typeof item === 'object' &&
          typeof (item as SavedSpark).token === 'string' &&
          typeof (item as SavedSpark).at === 'number' &&
          (item as SavedSpark).at > cutoff,
      )
      .slice(0, MAX_SAVED_SPARKS);
  } catch {
    return [];
  }
}

export function saveSparks(sparks: SavedSpark[]): void {
  write(SPARKS_KEY, sparks.slice(0, MAX_SAVED_SPARKS));
}

/** Adds or refreshes a spark, newest first. */
export function rememberSpark(sparks: SavedSpark[], entry: SavedSpark): SavedSpark[] {
  const next = [entry, ...sparks.filter((spark) => spark.token !== entry.token)];
  return next.slice(0, MAX_SAVED_SPARKS);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function yesterday(): string {
  return new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
}

/** Advances the daily streak. Call once per completed conversation. */
export function bumpStreak(stats: PlayerStats): PlayerStats {
  const day = today();
  if (stats.lastPlayed === day) return stats;
  const streak = stats.lastPlayed === yesterday() ? stats.streak + 1 : 1;
  return { ...stats, streak, lastPlayed: day };
}

export interface Badge {
  id: string;
  label: string;
  description: string;
  emoji: string;
  earned: (stats: PlayerStats) => boolean;
}

export const BADGES: Badge[] = [
  {
    id: 'first-contact',
    label: 'First contact',
    description: 'Talk to your first stranger.',
    emoji: '👋',
    earned: (s) => s.met >= 1,
  },
  {
    id: 'social-butterfly',
    label: 'Social butterfly',
    description: 'Meet 10 people.',
    emoji: '🦋',
    earned: (s) => s.met >= 10,
  },
  {
    id: 'centurion',
    label: 'Centurion',
    description: 'Meet 100 people.',
    emoji: '💯',
    earned: (s) => s.met >= 100,
  },
  {
    id: 'spark',
    label: 'Spark',
    description: 'Get your first mutual like.',
    emoji: '✨',
    earned: (s) => s.sparks >= 1,
  },
  {
    id: 'magnetic',
    label: 'Magnetic',
    description: 'Collect 25 sparks.',
    emoji: '🧲',
    earned: (s) => s.sparks >= 25,
  },
  {
    id: 'overtime',
    label: 'Overtime',
    description: 'Both of you choose to stay.',
    emoji: '⏳',
    earned: (s) => s.stays >= 1,
  },
  {
    id: 'reunion',
    label: 'Reunion',
    description: 'Find a spark again after the call ended.',
    emoji: '🔁',
    earned: (s) => s.reunions >= 1,
  },
  {
    id: 'deep-talk',
    label: 'Deep talk',
    description: 'Hold a single conversation for 10 minutes.',
    emoji: '🌊',
    earned: (s) => s.longest >= 600,
  },
  {
    id: 'globetrotter',
    label: 'Globetrotter',
    description: 'Meet people from 10 countries.',
    emoji: '🌍',
    earned: (s) => s.countries.length >= 10,
  },
  {
    id: 'regular',
    label: 'Regular',
    description: 'Keep a 7 day streak.',
    emoji: '🔥',
    earned: (s) => s.streak >= 7,
  },
];

/** Returns the badges newly unlocked by the given stats. */
export function newlyEarnedBadges(stats: PlayerStats): Badge[] {
  return BADGES.filter((badge) => !stats.badges.includes(badge.id) && badge.earned(stats));
}
